import React, { useEffect, useCallback } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AutoAwesome, Close } from '@mui/icons-material';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { HitlInterruptPanel } from './hitl/HitlInterruptPanel';
import { ApprovalInterruptPanel } from './hitl/ApprovalInterruptPanel';
import { buildHitlUserInput, type HitlAnswerDraft } from './hitl/types';
import { useChatAgent, type ChatAgentOptions } from '@/components/chat/hooks/useChatAgent';

interface ChatPanelProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  reportId: string;
  reportType: string;
  narrativeOnly?: boolean;
  evidenceIds?: string[];
  handleSaveChanges?: () => Promise<void>;
  fetchReportData?: () => void;
  showError?: (message: string) => void;
  onSendMessageRefReady?: (sendFn: (message: string) => Promise<void>) => void;
  /** Non-report invoke target (see ChatAgentOptions). Must be memoized. */
  options?: ChatAgentOptions;
  /** Card embeds render their own heading; skips the built-in header row. */
  hideHeader?: boolean;
  /** Replaces the default empty-thread hint text. */
  emptyStateHint?: string;
  /**
   * Hides the file-attach affordance. For invoke targets with no attachment
   * expansion (e.g. /incidents/invoke), where uploads would silently never
   * reach the model. Report chat surfaces keep the default (false).
   */
  disableAttachments?: boolean;
  /** Optional when hideHeader — the close affordance lives in the header. */
  onClose?: () => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = (props) => {
  const theme = useTheme();
  const {
    turnGroups,
    sendMessage,
    cancelStream,
    isStreaming,
    status,
    isLoadingHistory,
    pendingHitlInterrupts,
    hitlDrafts,
    handleHitlDraftChange,
    pendingApprovalInterrupts,
    approvalDecisions,
    handleApprovalDecisionChange,
    submitHitlResume,
    isSubmittingHitl,
    hasActiveInterrupts,
  } = useChatAgent({
    reportId: props.reportId,
    reportType: props.reportType,
    narrativeOnly: props.narrativeOnly,
    evidenceIds: props.evidenceIds,
    handleSaveChanges: props.handleSaveChanges,
    fetchReportData: props.fetchReportData,
    showError: props.showError,
    options: props.options,
  });

  // Expose sendMessage to parent via callback when ready
  const { onSendMessageRefReady } = props;
  useEffect(() => {
    if (onSendMessageRefReady) {
      onSendMessageRefReady(sendMessage);
    }
  }, [onSendMessageRefReady, sendMessage]);

  // Build and submit HITL responses. `overrides` lets card-level submit
  // affordances (e.g. PlanApprovalCard's Approve button) pass an
  // authoritative draft for an interruptId without waiting for React's
  // setState propagation through hitlDrafts.
  const handleHitlSubmit = useCallback(async (
    overrides: Record<string, HitlAnswerDraft> = {}
  ) => {
    const hitlResponses: Array<{ interruptId: string; userInput: unknown }> = [];

    for (const interrupt of pendingHitlInterrupts) {
      const draft = overrides[interrupt.interruptId] ?? hitlDrafts[interrupt.interruptId];
      if (!draft) continue;
      const result = buildHitlUserInput(interrupt, draft);
      if (result.ok) {
        hitlResponses.push({ interruptId: interrupt.interruptId, userInput: result.userInput });
      }
    }

    const approvalPayloads = Object.entries(approvalDecisions)
      .filter(([, decision]) => decision !== undefined)
      .map(([interruptId, decision]) => ({
        interruptId,
        decisions: [decision!],
      }));

    await submitHitlResume(hitlResponses, approvalPayloads);
  }, [pendingHitlInterrupts, hitlDrafts, approvalDecisions, submitHitlResume]);

  return (
    <Paper
      elevation={0}
      square
      sx={{
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      {!props.hideHeader && (
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AutoAwesome color="primary" fontSize="small" />
            AI Assistant
          </Typography>
          {props.onClose && (
            <IconButton size="small" onClick={props.onClose}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <ChatMessageList
          turnGroups={turnGroups}
          status={status}
          isLoadingHistory={isLoadingHistory}
          emptyStateText={props.emptyStateHint}
        />
      </Box>

      <Box sx={{ zIndex: 10, px: hasActiveInterrupts ? 2 : 0, pb: hasActiveInterrupts ? 1 : 0, pt: hasActiveInterrupts ? 2 : 0, borderTop: hasActiveInterrupts ? `1px solid ${theme.palette.divider}` : 'none', bgcolor: 'background.paper' }}>
        {hasActiveInterrupts ? (
          <>
            {pendingHitlInterrupts.length > 0 && (
              <HitlInterruptPanel
                interrupts={pendingHitlInterrupts}
                drafts={hitlDrafts}
                onDraftChange={handleHitlDraftChange}
                onSubmit={handleHitlSubmit}
                onCancel={cancelStream}
                isSubmitting={isSubmittingHitl}
              />
            )}
            {pendingApprovalInterrupts.length > 0 && (
              <ApprovalInterruptPanel
                interrupts={pendingApprovalInterrupts}
                decisions={approvalDecisions}
                onDecisionChange={handleApprovalDecisionChange}
                onSubmit={handleHitlSubmit}
                onCancel={cancelStream}
                isSubmitting={isSubmittingHitl}
              />
            )}
          </>
        ) : (
          <ChatInput
            onSend={sendMessage}
            onCancel={cancelStream}
            disabled={isStreaming || isLoadingHistory}
            isStreaming={isStreaming}
            showError={props.showError}
            disableAttachments={props.disableAttachments}
          />
        )}
      </Box>
    </Paper>
  );
};
